import React, { useState, useEffect } from "react";
import {
	CraftModal,
	CraftModalHeader,
	CraftModalBody,
	CraftModalFooter,
	CraftButton,
} from "@craft-design-system/core-components-react";
import {
	Calendar,
	Users,
	MessageSquare,
	ToggleLeft,
	Flag,
	Tag,
} from "lucide-react";
import { format } from "date-fns";

// BTT Status options for Serie tracking (same as SerieTrackingDataTable)
const STATUS_BTT_OPTIONS = [
	"Request SF",
	"MET Late",
	"Risk PET",
	"Deleted",
	"AC",
];

const SerieBulkEditModal = ({
	open,
	onClose,
	selectedItems,
	onSave,
	isSaving = false,
	processingProgress = null,
	getUserDisplayName,
}) => {
	// Helper to safely parse dates
	const safeDateParse = (dateValue) => {
		if (!dateValue) return null;
		try {
			const date = new Date(dateValue);
			return !isNaN(date.getTime()) ? date : null;
		} catch {
			return null;
		}
	};

	// Bulk edit field state
	const [bulkEditData, setBulkEditData] = useState({
		flagging: null, // 'Y' | 'N' | null
		status_toggle: null, // null | 'in Arbeit' | 'erledigt'
		btt_status: [], // array of selected options
		responsible: "",
		followup_date: null,
		comment: "",
		tags: "", // comma-separated tag string
	});

	// Which fields are enabled for bulk update
	const [fieldChanges, setFieldChanges] = useState({
		flagging: false,
		status_toggle: false,
		btt_status: false,
		responsible: false,
		followup_date: false,
		comment: false,
		tags: false,
	});

	// Reset on open
	useEffect(() => {
		if (open) {
			setBulkEditData({
				flagging: null,
				status_toggle: null,
				btt_status: [],
				responsible: "",
				followup_date: null,
				comment: "",
				tags: "",
			});
			setFieldChanges({
				flagging: false,
				status_toggle: false,
				btt_status: false,
				responsible: false,
				followup_date: false,
				comment: false,
				tags: false,
			});
		}
	}, [open]);

	const handleFieldChange = (field, value) => {
		setBulkEditData((prev) => ({ ...prev, [field]: value }));
		setFieldChanges((prev) => ({ ...prev, [field]: true }));
	};

	const handleToggleEnable = (field, enabled) => {
		setFieldChanges((prev) => ({ ...prev, [field]: enabled }));
		if (!enabled) {
			// Reset value when disabling
			setBulkEditData((prev) => ({
				...prev,
				[field]:
					field === "btt_status"
						? []
						: field === "followup_date"
							? null
							: field === "flagging" || field === "status_toggle"
								? null
								: "",
			}));
		}
	};

	const handleSave = () => {
		const currentUser = getUserDisplayName ? getUserDisplayName() : "User";
		const changes = {};

		if (fieldChanges.flagging && bulkEditData.flagging !== null) {
			changes.flagging = bulkEditData.flagging;
		}
		if (fieldChanges.status_toggle) {
			changes.status_toggle = bulkEditData.status_toggle; // can be null
		}
		if (fieldChanges.btt_status) {
			if (
				Array.isArray(bulkEditData.btt_status) &&
				bulkEditData.btt_status.length > 0
			) {
				if (bulkEditData.btt_status.includes("SET_TO_EMPTY")) {
					changes.btt_status = null;
				} else {
					changes.btt_status = bulkEditData.btt_status.join(", ");
				}
			} else {
				changes.btt_status = null;
			}
		}
		if (fieldChanges.responsible) {
			changes.responsible =
				bulkEditData.responsible === "SET_TO_NULL"
					? null
					: bulkEditData.responsible;
		}
		if (fieldChanges.followup_date) {
			if (bulkEditData.followup_date === "SET_TO_NULL") {
				changes.followup_date = null;
			} else if (
				bulkEditData.followup_date instanceof Date &&
				!isNaN(bulkEditData.followup_date.getTime())
			) {
				changes.followup_date = format(
					bulkEditData.followup_date,
					"yyyy-MM-dd",
				);
			} else {
				changes.followup_date = null;
			}
		}
		if (fieldChanges.comment) {
			changes.comment = bulkEditData.comment;
			changes.user_name = currentUser;
		}
		if (fieldChanges.tags) {
			changes.tags =
				bulkEditData.tags === "SET_TO_NULL"
					? null
					: bulkEditData.tags.trim() || null;
		}

		onSave(selectedItems, changes);
	};

	const hasChanges = Object.keys(fieldChanges).some((field) => {
		if (!fieldChanges[field]) return false;
		const value = bulkEditData[field];
		switch (field) {
			case "flagging":
				return value === "Y" || value === "N";
			case "status_toggle":
				return true; // null is valid (clearing)
			case "btt_status":
				return true;
			case "responsible":
				return value === "SET_TO_NULL" || (value && value.trim() !== "");
			case "followup_date":
				return (
					value === "SET_TO_NULL" ||
					(value instanceof Date && !isNaN(value.getTime()))
				);
			case "comment":
				return true;
			case "tags":
				return value === "SET_TO_NULL" || (value && value.trim() !== "");
			default:
				return value !== null && value !== undefined && value !== "";
		}
	});

	// Date <-> native input helpers
	const dateInputValue = (v) => {
		const d = safeDateParse(v);
		return d ? format(d, "yyyy-MM-dd") : "";
	};
	const dateFromInput = (val) => (val ? new Date(`${val}T00:00:00`) : null);

	// BTT multi-select helpers
	const bttValues = Array.isArray(bulkEditData.btt_status)
		? bulkEditData.btt_status
		: [];
	const bttIsEmptySentinel = bttValues.includes("SET_TO_EMPTY");
	const toggleBttOption = (option) => {
		const base = bttValues.filter((v) => v !== "SET_TO_EMPTY");
		const next = base.includes(option)
			? base.filter((v) => v !== option)
			: [...base, option];
		handleFieldChange("btt_status", next);
	};

	const s = {
		shell: {
			width: "1200px",
			maxWidth: "100%",
			display: "flex",
			flexDirection: "column",
			maxHeight: "74vh",
			position: "relative",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		scrollArea: {
			flex: 1,
			minHeight: 0,
			overflowY: "auto",
			paddingRight: "2px",
		},
		sectionHead: {
			display: "flex",
			alignItems: "center",
			gap: "8px",
			marginBottom: "8px",
			marginTop: "4px",
		},
		sectionTitle: {
			margin: 0,
			fontSize: "15px",
			color: "#1e2832",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
		},
		emptyBox: {
			padding: "40px",
			textAlign: "center",
			color: "#999",
			backgroundColor: "#f9f9f9",
			border: "1px dashed #ddd",
			borderRadius: "6px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		tableWrap: {
			maxHeight: "220px",
			overflow: "auto",
			border: "1px solid #e5e7eb",
			borderRadius: "8px",
			marginBottom: "20px",
			backgroundColor: "white",
		},
		table: {
			width: "100%",
			borderCollapse: "collapse",
			fontSize: "12px",
		},
		th: {
			textAlign: "left",
			padding: "9px 12px",
			backgroundColor: "#f8f9fa",
			borderBottom: "1px solid #e5e7eb",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			fontSize: "11px",
			color: "#374151",
			position: "sticky",
			top: 0,
			zIndex: 1,
			whiteSpace: "nowrap",
		},
		td: {
			padding: "7px 12px",
			borderBottom: "1px solid #f1f5f9",
			color: "#374151",
			whiteSpace: "nowrap",
		},
		grid: {
			display: "grid",
			gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
			gap: "14px",
			alignItems: "start",
		},
		card: {
			border: "1px solid #e5e7eb",
			borderRadius: "8px",
			overflow: "hidden",
			backgroundColor: "#fbfbfb",
			transition: "border-color 0.2s ease, box-shadow 0.2s ease",
		},
		cardActive: {
			border: "1px solid #1e2832",
			borderRadius: "8px",
			overflow: "hidden",
			backgroundColor: "white",
			boxShadow: "0 4px 14px rgba(30,40,50,0.10)",
		},
		cardHeader: (active) => ({
			display: "flex",
			alignItems: "center",
			gap: "10px",
			padding: "12px 14px",
			cursor: "pointer",
			backgroundColor: active ? "#ffffff" : "#f8f9fa",
			borderBottom: active ? "1px solid #eee" : "1px solid transparent",
		}),
		cardLabel: (active, accent) => ({
			fontSize: "14px",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			color: active ? accent : "#374151",
		}),
		checkbox: {
			transform: "scale(1.2)",
			accentColor: "#cc0000",
			pointerEvents: "none",
		},
		cardBody: {
			padding: "14px",
			display: "flex",
			flexDirection: "column",
			gap: "12px",
		},
		nullRow: {
			display: "flex",
			alignItems: "center",
			gap: "8px",
			cursor: "pointer",
			fontSize: "13px",
			color: "#dc2626",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		input: {
			width: "100%",
			boxSizing: "border-box",
			padding: "8px 10px",
			border: "1px solid #ccc",
			borderRadius: "6px",
			fontSize: "13px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		select: {
			width: "100%",
			boxSizing: "border-box",
			padding: "8px 10px",
			border: "1px solid #ccc",
			borderRadius: "6px",
			fontSize: "13px",
			backgroundColor: "white",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		textarea: {
			width: "100%",
			boxSizing: "border-box",
			padding: "8px 10px",
			border: "1px solid #ccc",
			borderRadius: "6px",
			fontSize: "13px",
			resize: "vertical",
			minHeight: "90px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		bttBox: {
			border: "1px solid #e5e7eb",
			borderRadius: "6px",
			overflow: "hidden",
			maxHeight: "200px",
			overflowY: "auto",
		},
		bttRow: (checked, danger) => ({
			display: "flex",
			alignItems: "center",
			gap: "8px",
			padding: "7px 12px",
			borderBottom: "1px solid #f0f0f0",
			cursor: "pointer",
			fontSize: "13px",
			color: danger ? "#dc2626" : "#333",
			backgroundColor: checked ? (danger ? "#fee2e2" : "#f0f8ff") : "white",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		}),
		tagChip: {
			display: "inline-block",
			backgroundColor: "#1e2832",
			color: "white",
			borderRadius: "12px",
			padding: "2px 10px",
			fontSize: "11px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		hint: {
			margin: 0,
			fontSize: "11px",
			color: "#9ca3af",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		footerText: {
			marginRight: "auto",
			fontSize: "13px",
			color: "#6b7280",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		overlay: {
			position: "absolute",
			inset: 0,
			backgroundColor: "rgba(255,255,255,0.95)",
			display: "flex",
			flexDirection: "column",
			justifyContent: "center",
			alignItems: "center",
			zIndex: 5,
		},
	};

	// Renders a toggleable field card (function, not a component, so text inputs
	// keep focus across re-renders).
	const renderField = (fieldKey, icon, label, accent, body) => {
		const active = fieldChanges[fieldKey];
		return (
			<div style={active ? s.cardActive : s.card}>
				<div
					style={s.cardHeader(active)}
					onClick={() => handleToggleEnable(fieldKey, !active)}
				>
					<input type="checkbox" checked={active} readOnly style={s.checkbox} />
					{icon(active, accent)}
					<span style={s.cardLabel(active, accent)}>{label}</span>
				</div>
				{active && <div style={s.cardBody}>{body}</div>}
			</div>
		);
	};

	const formatItemDate = (d) => {
		if (!d) return "-";
		try {
			const dt = new Date(d);
			return !isNaN(dt.getTime()) ? dt.toLocaleDateString() : "-";
		} catch {
			return "-";
		}
	};

	return (
		<CraftModal show={open} allowClickOut={false} onCraftClose={onClose}>
			<style>{`@keyframes spin { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }`}</style>
			<CraftModalHeader
				header="Bulk Edit"
				content={`${selectedItems.length} serie item(s) selected — pick the fields to update.`}
			/>
			<CraftModalBody>
				<div style={s.shell}>
					{/* Progress overlay */}
					{processingProgress && (
						<div style={s.overlay}>
							<div
								style={{
									width: "48px",
									height: "48px",
									border: "4px solid #f3f3f3",
									borderTop: "4px solid #cc0000",
									borderRadius: "50%",
									animation: "spin 1s linear infinite",
									marginBottom: "18px",
								}}
							/>
							<div
								style={{
									fontSize: "17px",
									color: "#1e2832",
									fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
									marginBottom: "8px",
								}}
							>
								Processing Bulk Updates…
							</div>
							<div style={{ fontSize: "13px", color: "#6b7280" }}>
								Updating {processingProgress.total} selected items
							</div>
							<div
								style={{
									width: "260px",
									height: "8px",
									backgroundColor: "#f3f4f6",
									borderRadius: "4px",
									margin: "16px 0",
									overflow: "hidden",
									border: "1px solid #e5e7eb",
								}}
							>
								<div
									style={{
										width: `${(processingProgress.current / processingProgress.total) * 100}%`,
										height: "100%",
										backgroundColor: "#cc0000",
										transition: "width 0.3s ease",
									}}
								/>
							</div>
							<div style={{ fontSize: "12px", color: "#9ca3af" }}>
								{Math.round(
									(processingProgress.current / processingProgress.total) * 100,
								)}
								% Complete
							</div>
							{processingProgress.currentOperation && (
								<div
									style={{
										marginTop: "12px",
										fontSize: "12px",
										color: "#6b7280",
										fontStyle: "italic",
									}}
								>
									{processingProgress.currentOperation}
								</div>
							)}
						</div>
					)}

					{selectedItems.length === 0 ? (
						<div style={s.emptyBox}>
							No items selected. Please select items from the table first.
						</div>
					) : (
						<div style={s.scrollArea}>
							{/* Selected Items Table */}
							<div style={s.sectionHead}>
								<span style={{ fontSize: "16px" }}>📋</span>
								<h3 style={s.sectionTitle}>
									Selected Items ({selectedItems.length})
								</h3>
							</div>
							<div style={s.tableWrap}>
								<table style={s.table}>
									<thead>
										<tr>
											<th style={s.th}>S.No</th>
											<th style={s.th}>SNR</th>
											<th style={s.th}>Project</th>
											<th style={s.th}>Description</th>
											<th style={s.th}>Responsible</th>
											<th style={s.th}>Follow-up Date</th>
											<th style={s.th}>BTT Status</th>
											<th style={s.th}>Status Toggle</th>
											<th style={s.th}>Flagging</th>
											<th style={s.th}>Tags</th>
										</tr>
									</thead>
									<tbody>
										{selectedItems.map((item, index) => (
											<tr key={index}>
												<td style={s.td}>{index + 1}</td>
												<td
													style={{
														...s.td,
														fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
													}}
												>
													{item.SNR || item.snr || "-"}
												</td>
												<td style={s.td}>
													{item.project || item.snr_anlage_projnr || "-"}
												</td>
												<td style={s.td}>
													{item.description || item.bend || "-"}
												</td>
												<td style={s.td}>
													{item.status?.responsible ||
														item.atr_verantwortlich ||
														"-"}
												</td>
												<td style={s.td}>
													{formatItemDate(
														item.status?.followUp || item.atr_wiedervorlage,
													)}
												</td>
												<td style={s.td}>{item.btt_status || "-"}</td>
												<td style={s.td}>{item.status_toggle || "-"}</td>
												<td style={s.td}>
													<span
														style={{
															display: "inline-block",
															fontSize: "11px",
															fontWeight: "600",
														}}
													>
														{item.flagging === "Y" ? "Flagged" : "Unflagged"}
													</span>
												</td>
												<td style={s.td}>
													{item.tags
														? item.tags
																.split(",")
																.map((t) => t.trim())
																.filter(Boolean)
																.map((t) => (
																	<span
																		key={t}
																		style={{
																			display: "inline-block",
																			backgroundColor: "#1e2832",
																			color: "white",
																			borderRadius: "10px",
																			padding: "1px 7px",
																			fontSize: "10px",
																			marginRight: "3px",
																			marginBottom: "2px",
																		}}
																	>
																		{t}
																	</span>
																))
														: "-"}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>

							{/* Bulk Edit Fields */}
							<div style={s.sectionHead}>
								<span style={{ fontSize: "16px" }}>⚙️</span>
								<h3 style={s.sectionTitle}>Select Fields to Update</h3>
							</div>
							<div style={s.grid}>
								{/* Flagging */}
								{renderField(
									"flagging",
									(active, accent) => (
										<Flag size={18} color={active ? accent : "#6b7280"} />
									),
									"Flagging",
									"#cc0000",
									<select
										style={s.select}
										value={bulkEditData.flagging || ""}
										onChange={(e) =>
											handleFieldChange("flagging", e.target.value)
										}
									>
										<option value="" disabled>
											Select Flagging Status
										</option>
										<option value="Y">🚩 Flagged (Y)</option>
										<option value="N">Unflagged (N)</option>
									</select>,
								)}

								{/* BTT Status */}
								{renderField(
									"btt_status",
									() => null,
									"BTT Status",
									"#cc0000",
									<div style={s.bttBox}>
										<label
											style={s.bttRow(bttIsEmptySentinel, true)}
											onClick={(e) => {
												e.preventDefault();
												handleFieldChange(
													"btt_status",
													bttIsEmptySentinel ? [] : ["SET_TO_EMPTY"],
												);
											}}
										>
											<input
												type="checkbox"
												checked={bttIsEmptySentinel}
												readOnly
											/>
											🗑️ Set to Empty/NULL
										</label>
										{STATUS_BTT_OPTIONS.map((opt) => {
											const checked =
												!bttIsEmptySentinel && bttValues.includes(opt);
											return (
												<label
													key={opt}
													style={s.bttRow(checked, false)}
													onClick={(e) => {
														e.preventDefault();
														toggleBttOption(opt);
													}}
												>
													<input type="checkbox" checked={checked} readOnly />
													{opt}
												</label>
											);
										})}
									</div>,
								)}

								{/* Responsible */}
								{renderField(
									"responsible",
									(active, accent) => (
										<Users size={18} color={active ? accent : "#6b7280"} />
									),
									"Responsible (Resp:)",
									"#cc0000",
									<>
										<label style={s.nullRow}>
											<input
												type="checkbox"
												checked={bulkEditData.responsible === "SET_TO_NULL"}
												onChange={(e) =>
													handleFieldChange(
														"responsible",
														e.target.checked ? "SET_TO_NULL" : "",
													)
												}
											/>
											🗑️ Set to Empty/NULL (clear responsible)
										</label>
										{bulkEditData.responsible !== "SET_TO_NULL" && (
											<input
												type="text"
												style={s.input}
												placeholder="Enter responsible person name"
												value={bulkEditData.responsible}
												onChange={(e) => {
													const cleaned = e.target.value.replace(
														/[^a-zA-Z0-9\s.\-_'/]/g,
														"",
													);
													handleFieldChange("responsible", cleaned);
												}}
											/>
										)}
									</>,
								)}

								{/* Follow-up Date */}
								{renderField(
									"followup_date",
									(active, accent) => (
										<Calendar size={18} color={active ? accent : "#6b7280"} />
									),
									"Date (Follow-up Date)",
									"#cc0000",
									<>
										<label style={s.nullRow}>
											<input
												type="checkbox"
												checked={bulkEditData.followup_date === "SET_TO_NULL"}
												onChange={(e) =>
													handleFieldChange(
														"followup_date",
														e.target.checked ? "SET_TO_NULL" : null,
													)
												}
											/>
											🗑️ Set to Empty/NULL (clear follow-up date)
										</label>
										{bulkEditData.followup_date !== "SET_TO_NULL" && (
											<input
												type="date"
												style={s.input}
												value={dateInputValue(bulkEditData.followup_date)}
												onChange={(e) =>
													handleFieldChange(
														"followup_date",
														dateFromInput(e.target.value),
													)
												}
											/>
										)}
									</>,
								)}


								{/* Status Toggle */}
								{renderField(
									"status_toggle",
									(active, accent) => (
										<ToggleLeft size={18} color={active ? accent : "#6b7280"} />
									),
									"Status Toggle (None / in Arbeit / erledigt)",
									"#cc0000",
									<select
										style={s.select}
										value={
											bulkEditData.status_toggle === null
												? "null"
												: bulkEditData.status_toggle || "null"
										}
										onChange={(e) =>
											handleFieldChange(
												"status_toggle",
												e.target.value === "null" ? null : e.target.value,
											)
										}
									>
										<option value="null">None (Empty/Null)</option>
										<option value="in Arbeit">in Arbeit (In Progress)</option>
										<option value="erledigt">erledigt (Completed)</option>
									</select>,
								)}

								{/* Tags */}
								{renderField(
									"tags",
									(active, accent) => (
										<Tag size={18} color={active ? accent : "#6b7280"} />
									),
									"Tags",
									"#cc0000",
									<>
										<label style={s.nullRow}>
											<input
												type="checkbox"
												checked={bulkEditData.tags === "SET_TO_NULL"}
												onChange={(e) =>
													handleFieldChange(
														"tags",
														e.target.checked ? "SET_TO_NULL" : "",
													)
												}
											/>
											🗑️ Set to Empty/NULL (clear all tags)
										</label>
										{bulkEditData.tags !== "SET_TO_NULL" && (
											<>
												<input
													type="text"
													style={s.input}
													placeholder="e.g. urgent, review, hold"
													value={bulkEditData.tags}
													onChange={(e) =>
														handleFieldChange("tags", e.target.value)
													}
												/>
												{bulkEditData.tags.trim() && (
													<div
														style={{
															display: "flex",
															flexWrap: "wrap",
															gap: "4px",
														}}
													>
														{bulkEditData.tags
															.split(",")
															.map((t) => t.trim())
															.filter(Boolean)
															.map((t) => (
																<span key={t} style={s.tagChip}>
																	{t}
																</span>
															))}
													</div>
												)}
												<p style={s.hint}>
													These tags will replace existing tags on all selected
													items.
												</p>
											</>
										)}
									</>,
								)}

																{/* Comment (spans two columns) */}
								<div style={{ gridColumn: "1 / -1" }}>
									{renderField(
										"comment",
										(active, accent) => (
											<MessageSquare
												size={18}
												color={active ? accent : "#6b7280"}
											/>
										),
										"Add Comment (added to all selected items)",
										"#cc0000",
										<textarea
											style={s.textarea}
											placeholder="This comment will be added to all selected series items."
											value={bulkEditData.comment}
											onChange={(e) =>
												handleFieldChange("comment", e.target.value)
											}
										/>,
									)}
								</div>
							</div>
						</div>
					)}
				</div>
			</CraftModalBody>
			<CraftModalFooter>
				<span style={s.footerText}>
					{hasChanges
						? `${Object.values(fieldChanges).filter(Boolean).length} field(s) will be updated for ${selectedItems.length} item(s)`
						: "No fields selected for update"}
				</span>
				<CraftButton
					variant="plain"
					size="m"
					onClick={onClose}
					disabled={isSaving}
				>
					Cancel
				</CraftButton>
				<CraftButton
					variant="primary"
					size="m"
					onClick={handleSave}
					disabled={!hasChanges || selectedItems.length === 0 || isSaving}
				>
					{isSaving
						? "Applying…"
						: `Apply to ${selectedItems.length} Item${selectedItems.length !== 1 ? "s" : ""}`}
				</CraftButton>
			</CraftModalFooter>
		</CraftModal>
	);
};

export default SerieBulkEditModal;
